import { OAuthStateData } from './useClickUp.types';

const CLICKUP_CLIENT_ID = import.meta.env.VITE_CLICKUP_CLIENT_ID;
const OAUTH_STATE_STORAGE_KEY = 'clickup_oauth_state';

export function getClickUpOAuthUrl(redirectUri: string): string {
  if (!CLICKUP_CLIENT_ID) {
    console.error('VITE_CLICKUP_CLIENT_ID is not set');
    return '';
  }
  return `https://app.clickup.com/api?client_id=${CLICKUP_CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}`;
}

export function storeOAuthState(stateData: OAuthStateData): void {
  try {
    sessionStorage.setItem(OAUTH_STATE_STORAGE_KEY, JSON.stringify(stateData));
  } catch (error) {
    console.error('Failed to store OAuth state in session storage:', error);
  }
}

export function getStoredOAuthState(): OAuthStateData | null {
  try {
    const stored = sessionStorage.getItem(OAUTH_STATE_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as OAuthStateData;
  } catch (error) {
    console.error('Failed to retrieve OAuth state from session storage:', error);
    return null;
  }
}

export function clearOAuthState(): void {
  try {
    sessionStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear OAuth state from session storage:', error);
  }
}

export function validateOAuthState(urlState: string | null): { valid: boolean; error?: string } {
  if (!urlState) {
    return { valid: false, error: 'Missing state parameter in OAuth callback' };
  }

  const storedState = getStoredOAuthState();
  if (!storedState) {
    return { valid: false, error: 'No OAuth state found. Please try connecting again.' };
  }

  if (storedState.state !== urlState) {
    console.error('[OAuth Security] State mismatch - possible CSRF attack');
    return { valid: false, error: 'Invalid state parameter. Please try connecting again.' };
  }

  if (new Date(storedState.expiresAt) < new Date()) {
    console.error('[OAuth Security] State has expired');
    clearOAuthState();
    return { valid: false, error: 'OAuth session expired. Please try connecting again.' };
  }

  return { valid: true };
}

export function isReauthRequired(error: unknown): boolean {
  if (!error) return false;

  if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, unknown>;
    if (errObj.requires_reauth === true) return true;
    if (errObj.error_code === 'TOKEN_REVOKED') return true;
    if (errObj.error_code === 'TOKEN_REFRESH_FAILED') return true;
    if (errObj.error_code === 'CONNECTION_NOT_FOUND') return true;
  }

  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    if (msg.includes('reconnect') || msg.includes('re-auth') || msg.includes('revoked')) {
      return true;
    }
  }

  return false;
}

export function getTokenErrorMessage(error: unknown): string {
  if (!error) return 'Unknown error';

  if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, unknown>;
    if (typeof errObj.error === 'string') return errObj.error;
  }

  if (error instanceof Error) return error.message;

  return 'An error occurred with your ClickUp connection';
}